import { useState, useEffect } from 'react'
import { Truck, Info, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { leadsAPI } from '../api'
import type { BestandStatus, OvrigKjoretoy, OvrigTypeGroup, OrgLeasingselskap } from '../api'
import { formatNorwegianDate, formatDrivstoff } from '../../../utils/dateUtils'
import EierhistorikkKnapp from '../eierhistorikk/EierhistorikkKnapp'
import { useKanSeEierhistorikk } from '../eierhistorikk/tilgang'
import { useLatest } from '../../Bruktbil/useLatest'
import { osloYmd, osloDaysAgo } from './bestandFreshness'
import LeadsTableExport from '../LeadsTableExport'
import { LEADS_EXPORT_LIMIT } from '../eksportTilgang'
import type { ColumnDefinition } from '../../../utils/exportToCsv'

interface Props {
  orgNr: string
  /** Shown as the current owner in the eierhistorikk panel. */
  orgNavn?: string | null
}

const PAGE_SIZE = 50

type SortKey =
  | 'kjennemerke' | 'merke' | 'modell' | 'type' | 'drivstofftype'
  | 'registrert_forste_gang' | 'registrert_forste_gang_pa_eier'
  | 'registreringsstatus' | 'leasing_company'

interface OvrigeQuery {
  type: OvrigTypeGroup | ''; merke: string; leasing: string; leasingCompany: string
  sortBy: SortKey; sortDir: 'asc' | 'desc'
}

/** The grid's filters and sort, for one page or for the export (#885). */
function ovrigeParams(q: OvrigeQuery, limit: number, offset: number) {
  return {
    type: q.type || undefined,
    merke: q.merke || undefined,
    leasing: q.leasing === '' ? undefined : q.leasing === 'leaset',
    leasing_company: q.leasingCompany || undefined,
    sort_by: q.sortBy,
    sort_dir: q.sortDir,
    limit,
    offset,
  }
}

const exportDate = (v: string | null | undefined) => (v ? formatNorwegianDate(v) : '—')

/**
 * Vehicles the org holds outside personbil/varebil (#640). Only the bestand
 * sweep updates them, a few times a year per org, so the sweep date always
 * sits on top — and an org that has not been swept gets no table at all.
 */
export default function OvrigeKjoretoySection({ orgNr, orgNavn }: Props) {
  const { t } = useTranslation('leads')
  const [status, setStatus] = useState<BestandStatus | null>(null)
  const [statusError, setStatusError] = useState(false)

  useLatest(alive => {
    setStatus(null)
    setStatusError(false)
    leadsAPI.getBestandStatus(orgNr)
      .then(res => { if (alive()) setStatus(res.data) })
      .catch(() => { if (alive()) setStatusError(true) })
  }, [orgNr])

  if (statusError) {
    return <div className="card p-6 text-red-600 dark:text-red-400">{t('ovrige.statusError')}</div>
  }
  if (!status) {
    return <div className="card p-6"><div className="animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" /></div>
  }

  const checkedAt = status.last_checked_at
  const days = checkedAt ? osloDaysAgo(checkedAt) : 0
  const ago = days <= 0 ? t('ovrige.today') : days === 1 ? t('ovrige.oneDayAgo') : t('ovrige.daysAgo', { n: days })

  return (
    <div className="card p-6">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
        <Truck className="w-5 h-5 inline mr-2" />{t('ovrige.title')}
      </h2>

      <div data-testid="bestand-notice"
           className="flex gap-2 mb-4 p-3 rounded-sm bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-900 dark:text-blue-200">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        {checkedAt ? (
          <div>
            <p>{t('ovrige.checkedAt', { date: formatNorwegianDate(osloYmd(checkedAt)), ago })}</p>
            <p>{t('ovrige.onlyAtCheck')}</p>
            <p>{t('ovrige.cadence')}</p>
          </div>
        ) : (
          <p>{t('ovrige.notChecked')}</p>
        )}
      </div>

      {checkedAt && <OvrigeGrid orgNr={orgNr} orgNavn={orgNavn} />}
    </div>
  )
}

function OvrigeGrid({ orgNr, orgNavn }: Props) {
  const { t } = useTranslation('leads')
  const kanSeHistorikk = useKanSeEierhistorikk()
  const [vehicles, setVehicles] = useState<OvrigKjoretoy[]>([])
  const [total, setTotal] = useState(0)
  const [totalCapped, setTotalCapped] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [typeFilter, setTypeFilter] = useState<OvrigTypeGroup | ''>('')
  const [typeOptions, setTypeOptions] = useState<{ type: OvrigTypeGroup; car_count: number }[]>([])
  const [merkeFilter, setMerkeFilter] = useState('')
  const [merkeOptions, setMerkeOptions] = useState<{ merke: string; car_count: number }[]>([])
  const [leasingFilter, setLeasingFilter] = useState('')
  const [leasingCompanyFilter, setLeasingCompanyFilter] = useState('')
  const [leasingCompanyOptions, setLeasingCompanyOptions] = useState<OrgLeasingselskap[]>([])
  const [sortBy, setSortBy] = useState<SortKey>('registrert_forste_gang_pa_eier')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  useLatest(alive => {
    Promise.all([
      leadsAPI.getOvrigeTyper(orgNr),
      leadsAPI.getOvrigeMerker(orgNr),
      leadsAPI.getOvrigeLeasingselskap(orgNr),
    ])
      .then(([typer, merker, leasing]) => {
        if (!alive()) return
        setTypeOptions(typer.data.items)
        setMerkeOptions(merker.data.items)
        setLeasingCompanyOptions(leasing.data.items)
      })
      .catch(() => {})
  }, [orgNr])

  useEffect(() => { setPage(1) }, [typeFilter, merkeFilter, leasingFilter, leasingCompanyFilter, sortBy, sortDir])

  useLatest(alive => {
    setLoading(true)
    leadsAPI.getOvrigeKjoretoy(orgNr, ovrigeParams({
      type: typeFilter, merke: merkeFilter, leasing: leasingFilter,
      leasingCompany: leasingCompanyFilter, sortBy, sortDir,
    }, PAGE_SIZE, (page - 1) * PAGE_SIZE))
      .then(res => {
        if (!alive()) return
        setVehicles(res.data.items)
        setTotal(res.data.total)
        setTotalCapped(res.data.total_is_capped)
        setHasMore(res.data.has_more)
      })
      .catch(() => {})
      .finally(() => { if (alive()) setLoading(false) })
  }, [orgNr, page, typeFilter, merkeFilter, leasingFilter, leasingCompanyFilter, sortBy, sortDir])

  function handleSort(key: SortKey) {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortDir(key.startsWith('registrert_forste') || key === 'leasing_company' ? 'desc' : 'asc')
    }
  }

  function handleNullstill() {
    setTypeFilter('')
    setMerkeFilter('')
    setLeasingFilter('')
    setLeasingCompanyFilter('')
    setSortBy('registrert_forste_gang_pa_eier')
    setSortDir('desc')
  }

  const filtersActive = !!(typeFilter || merkeFilter || leasingFilter || leasingCompanyFilter
    || sortBy !== 'registrert_forste_gang_pa_eier' || sortDir !== 'desc')

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const exportColumns: ColumnDefinition[] = [
    { key: 'kjennemerke', label: t('common.kjennemerke') },
    { key: 'merke', label: t('common.merke'), format: v => v ?? '—' },
    { key: 'modell', label: t('common.modell'), format: v => v ?? '—' },
    { key: 'type', label: t('common.type'), format: v => v ?? '—' },
    { key: 'drivstofftype', label: t('vehicles.drivstoff'), format: v => formatDrivstoff(v) },
    { key: 'registrert_forste_gang', label: t('common.firstReg'), format: exportDate },
    { key: 'registrert_forste_gang_pa_eier', label: t('common.regPaEier'), format: exportDate },
    {
      key: 'registreringsstatus', label: t('ovrige.status'),
      format: v => (v === 'REGISTRERT' ? t('ovrige.registrert') : v === 'AVREGISTRERT' ? t('ovrige.avregistrert') : v ?? '—'),
    },
    { key: 'leasing_company_navn', label: t('vehicles.leasing'), format: v => v ?? '—' },
  ]

  const header = (label: string, key: SortKey) => (
    <SortHeader label={label} sortKey={key} current={sortBy} dir={sortDir} onSort={handleSort} />
  )

  return (
    <>
      <div className="flex items-center justify-end mb-3">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('common.totalCount', { n: totalCapped ? `${total}+` : total })}
        </span>
      </div>

      <div className="flex flex-nowrap items-center gap-3 mb-4 overflow-x-auto">
        <select value={merkeFilter} onChange={e => setMerkeFilter(e.target.value)} className="input-field text-sm">
          <option value="">{t('common.allMerker')}</option>
          {merkeOptions.map(m => (
            <option key={m.merke} value={m.merke}>{m.merke} ({m.car_count})</option>
          ))}
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as OvrigTypeGroup | '')}
                className="input-field text-sm" data-testid="ovrige-type-filter">
          <option value="">{t('common.allTypes')}</option>
          {typeOptions.map(o => (
            <option key={o.type} value={o.type}>{t(`ovrige.types.${o.type}`)} ({o.car_count})</option>
          ))}
        </select>
        <select value={leasingFilter} onChange={e => setLeasingFilter(e.target.value)} className="input-field text-sm">
          <option value="">{t('vehicles.eidOgLeaset')}</option>
          <option value="eid">{t('vehicles.kunEid')}</option>
          <option value="leaset">{t('vehicles.kunLeaset')}</option>
        </select>
        {leasingCompanyOptions.length > 0 && (
          <select value={leasingCompanyFilter} onChange={e => setLeasingCompanyFilter(e.target.value)} className="input-field text-sm">
            <option value="">{t('vehicles.allLeasingselskap')}</option>
            {leasingCompanyOptions.map(l => (
              <option key={l.org_nr} value={l.org_nr}>{l.navn} ({l.car_count})</option>
            ))}
          </select>
        )}
        {filtersActive && (
          <button onClick={handleNullstill}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline whitespace-nowrap">
            {t('common.reset')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
      ) : vehicles.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">{filtersActive ? t('vehicles.empty') : t('ovrige.empty')}</p>
      ) : (
        <>
          <LeadsTableExport
            rows={vehicles}
            columns={exportColumns}
            title={t('tableExport.orgTitle', { table: t('bedrift.vehicleTabs.ovrige'), navn: orgNavn ?? orgNr, orgNr })}
            loadData={() => leadsAPI.getOvrigeKjoretoy(orgNr, ovrigeParams({
              type: typeFilter, merke: merkeFilter, leasing: leasingFilter,
              leasingCompany: leasingCompanyFilter, sortBy, sortDir,
            }, LEADS_EXPORT_LIMIT, 0)).then(res => res.data.items)}
            capped={totalCapped}
            testId="bedrift-ovrige-export"
          />
          <div className="overflow-x-auto">
            <table className="data-table data-table-compact" data-testid="ovrige-table">
              <thead>
                <tr>
                  {header(t('common.kjennemerke'), 'kjennemerke')}
                  {header(t('common.merke'), 'merke')}
                  {header(t('common.modell'), 'modell')}
                  {header(t('common.type'), 'type')}
                  {header(t('vehicles.drivstoff'), 'drivstofftype')}
                  {header(t('common.firstReg'), 'registrert_forste_gang')}
                  {header(t('common.regPaEier'), 'registrert_forste_gang_pa_eier')}
                  {header(t('ovrige.status'), 'registreringsstatus')}
                  {header(t('vehicles.leasing'), 'leasing_company')}
                  {kanSeHistorikk && <th className="text-center whitespace-nowrap">{t('common.historikk')}</th>}
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v, idx) => (
                  <tr key={v.kjennemerke} className={idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}>
                    <td className="font-mono text-xs text-primary-600 dark:text-primary-400">{v.kjennemerke}</td>
                    <td className="text-sm">{v.merke ?? '—'}</td>
                    <td className="text-sm">{v.modell ?? '—'}</td>
                    <td className="text-xs">{v.type ?? '—'}</td>
                    <td className="text-xs text-gray-500">{formatDrivstoff(v.drivstofftype)}</td>
                    <td className="text-xs text-gray-500">{v.registrert_forste_gang ? formatNorwegianDate(v.registrert_forste_gang) : '—'}</td>
                    <td className="text-xs font-medium">{v.registrert_forste_gang_pa_eier ? formatNorwegianDate(v.registrert_forste_gang_pa_eier) : '—'}</td>
                    <td className="text-xs">
                      {v.registreringsstatus === 'REGISTRERT' ? t('ovrige.registrert')
                        : v.registreringsstatus === 'AVREGISTRERT' ? t('ovrige.avregistrert')
                        : (v.registreringsstatus ?? '—')}
                    </td>
                    <td className="text-xs">{v.leasing_company_navn ?? '—'}</td>
                    {kanSeHistorikk && (
                      <td className="text-center">
                        <EierhistorikkKnapp
                          kjennemerke={v.kjennemerke}
                          kuid={v.kuid}
                          registrertPaEier={v.registrert_forste_gang_pa_eier}
                          navaerendeEier={v.leasingbil ? v.leasing_company_navn : orgNavn}
                          merke={v.merke}
                          modell={v.modell}
                          registrertForsteGang={v.registrert_forste_gang}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(pages > 1 || hasMore) && (
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mt-4">
              <span>{t('common.pageOf', { page, pages: totalCapped ? `${pages}+` : pages })}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  {t('common.prev')}
                </button>
                <button onClick={() => setPage(p => p + 1)} disabled={!hasMore}
                  className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  {t('common.next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

function SortHeader({ label, sortKey, current, dir, onSort }: {
  label: string
  sortKey: SortKey
  current: SortKey
  dir: 'asc' | 'desc'
  onSort: (key: SortKey) => void
}) {
  const active = current === sortKey
  return (
    <th
      className="text-left whitespace-nowrap cursor-pointer select-none hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          dir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        ) : (
          <span className="w-3 h-3 inline-block" />
        )}
      </span>
    </th>
  )
}
